import { mdiDotsVertical } from '@mdi/js';
import { useTranslation } from 'react-i18next';
import { MenuItem } from '@mui/material';
import { useState } from 'react';

import {
    ListItem,
    ListItemMedia,
    ListItemSecondaryAction,
    ListItemText,
} from 'common/components/ListItem';
import {
    IReceivedCertificateFragment,
    useDeleteReceivedCertificateMutation,
} from 'graphql/types';
import { formatDate } from 'common/utils/formatDate';
import { DropdownMenu } from 'common/components/DropdownMenu';
import { IconButton } from 'common/components/IconButton';
import { useCanManageUser } from 'user/hooks';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useApolloError } from 'common/hooks/useApolloError';
import { ReceivedCertificateProgressChip } from 'common/components/ReceivedCertificateProgressChip';

interface IProps {
    receivedCertificate: IReceivedCertificateFragment;
    userId: string;
    onDelete?: () => void;
}

export const ReceivedCertificateListItem = ({
    receivedCertificate,
    userId,
    onDelete,
}: IProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();
    const { canManageUser } = useCanManageUser(userId);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [deleteReceivedCertificate, { loading }] =
        useDeleteReceivedCertificateMutation();

    const { title, thumbnail, date, expiryDate, file } =
        receivedCertificate || {};

    const startDate = formatDate(date, 'dd MMMM yyyy');
    const endDate = expiryDate ? formatDate(expiryDate, 'dd MMMM yyyy') : '';
    const dateDisplay = `${startDate} ${endDate ? `- ${endDate}` : ''}`;

    const handleClickDelete = async () => {
        try {
            await deleteReceivedCertificate({
                variables: {
                    ids: [receivedCertificate.id],
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        setShowDeleteDialog(false);

        displaySnackbar(translate('deleteReceivedCertificateSuccess'), {
            variant: 'success',
        });

        onDelete?.();
    };

    return (
        <>
            <ListItem px={0}>
                <ListItemMedia
                    color="primary"
                    image={thumbnail?.url || ''}
                    size="small"
                />

                <ListItemText primary={title} secondary={dateDisplay} />

                {!!file?.url && (
                    <ListItemSecondaryAction>
                        <DropdownMenu
                            anchor={
                                <IconButton
                                    color="inherit"
                                    iconPath={mdiDotsVertical}
                                    iconSize="2.4rem"
                                    sx={{ mr: 2 }}
                                />
                            }
                        >
                            <MenuItem
                                onClick={() => {
                                    file?.url &&
                                        window.open(file.url, '_blank');
                                }}
                            >
                                {translate('download')}
                            </MenuItem>

                            {canManageUser && (
                                <MenuItem
                                    onClick={() => setShowDeleteDialog(true)}
                                >
                                    {translate('deleteReceivedCertificate')}
                                </MenuItem>
                            )}
                        </DropdownMenu>
                    </ListItemSecondaryAction>
                )}

                <ReceivedCertificateProgressChip
                    endDate={expiryDate}
                    startDate={date}
                />
            </ListItem>

            <ConfirmDialog
                color="error"
                confirmText={translate(
                    'deleteReceivedCertificateDialog.confirmText'
                )}
                loading={loading}
                open={!!showDeleteDialog}
                title={translate('deleteReceivedCertificateDialog.title')}
                onCancel={() => setShowDeleteDialog(false)}
                onClose={() => setShowDeleteDialog(false)}
                onConfirm={handleClickDelete}
            >
                {translate('deleteReceivedCertificateDialog.text')}
            </ConfirmDialog>
        </>
    );
};
